import { SystemIcon } from '@storeblocks/icon';
import AlertInline from '@storeblocks/inline-alert';
import LinearLoadingIndicator from '@storeblocks/linear-loading-indicator';
import { type FC } from 'react';
import styled from 'styled-components';

import { Show } from '@/components/Show';
import { useHandleAsyncOperation } from '@/hooks/useHandleAsyncError';
import { useTexts } from '@/hooks/useTexts';
import { TrackingElement, useGetTrackId } from '@/tracking';

interface Props {
  filename: string;
  fileId: string;
  onDownload: (fileId: string) => Promise<unknown>;
}

export const ReportListItem: FC<Props> = ({
  filename,
  fileId,
  onDownload,
}: Props) => {
  const texts = useTexts();
  const getTrackId = useGetTrackId();

  const startDownload = async (): Promise<void> => {
    await onDownload(fileId);
  };

  const [handleDownloadOnClick, isDownloading, isError] =
    useHandleAsyncOperation(startDownload);

  const handleOnKeyDown = (e: React.KeyboardEvent<HTMLLIElement>): void => {
    if (e.key === 'Enter') {
      handleDownloadOnClick();
    }
  };

  return (
    <ListItem
      onClick={handleDownloadOnClick}
      onKeyDown={handleOnKeyDown}
      data-trackid={getTrackId('download-file', TrackingElement.Link)}
      data-trackid-fileid={fileId}
      tabIndex={0}
    >
      <LinkText className="font-body">{filename}</LinkText>

      <Show when={isError}>
        <AlertInline variant="error" title={texts.download.singleFileError} />
      </Show>

      <Show when={isDownloading}>
        <LinearLoadingWrapper>
          <LinearLoadingIndicator type="indeterminate" />
        </LinearLoadingWrapper>
      </Show>

      <Show when={!isError && !isDownloading}>
        <SystemIcon name="download" isPhosphor />
      </Show>
    </ListItem>
  );
};

const ListItem = styled.li`
  cursor: pointer;
  border-bottom: 1px solid var(--p-color-stroke-subtle-0);
  padding: 16px 0;

  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const LinkText = styled.span`
  text-decoration-line: underline;
`;

const LinearLoadingWrapper = styled.div`
  justify-content: flex-start;
  align-self: center;
`;
